import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { marketingPagesFor, toolPath } from "../../src/marketing-pages";
import { locale, lp, t } from "./i18n";

// Every free tool has one illustration in the same picture-book style (scripts/build-featured-tool-art.py).
// The home page shows them in one swipeable row, the /tools hub and each tool page reuse the same images.
/** Most used first; the home row follows this order. */
export const TOOL_ORDER = [
  "random-picker", "timer", "random-groups", "seating-chart", "times-tables", "scoreboard", "certificate-maker", "report-comments", "contact-book",
  "noise-meter", "zhuyin", "exam-countdown", "stamp-card", "writing-paper", "charades-cards", "guess-number", "1a2b", "mental-math", "tic-tac-toe", "random-number", "dice-coin",
  "poll", "attendance", "stopwatch", "duty-roster",
  // D-194 (2026-10-27): weak-SERP tools, appended until a few weeks of public_tool_usage say where they belong.
  "online-clock", "random-order", "school-bell", "hundreds-chart",
  // D-199: 班級成績統計 (teacher work after an exam).
  "grade-stats",
  // D-200 (2026-10-27): 日期計算機.
  "date-calculator",
  // D-201: bingo cards.
  "bingo-cards",
  // D-202 (2026-10-27): classroom brain games, appended the same way.
  "schulte-table", "tower-of-hanoi", "24-game",
  // D-203 (2026-10-27): protractor, metronome and 文章加注音 (Traditional Chinese only), appended the same way.
  "protractor", "metronome", "zhuyin-text",
  // D-198 (2026-10-27): tap-to-hear charts.
  "alphabet-chart", "kk-phonics",
  // D-251 (2026-11-01): 課表產生器.
  "timetable-maker",
] as const;
const artBase = (slug: string) => `/assets/forest-ui/marketing/tool-${slug}-v1`;

/** The tool's illustration; `small` uses the 480px file for thumbnails and card media. */
export function ToolArt({ slug, sizes, eager = false, className }: { slug: string; sizes: string; eager?: boolean; className?: string }) {
  return <img className={className} src={`${artBase(slug)}-480.webp`} srcSet={`${artBase(slug)}-480.webp 480w, ${artBase(slug)}.webp 768w`} sizes={sizes} width="768" height="512" alt="" loading={eager ? "eager" : "lazy"} decoding="async" />;
}

export const orderedTools = () => {
  const tools = marketingPagesFor(locale).tools;
  const rank = (slug: string) => { const index = (TOOL_ORDER as readonly string[]).indexOf(slug); return index < 0 ? TOOL_ORDER.length : index; };
  return [...tools].sort((a, b) => rank(a.slug) - rank(b.slug));
};

const DRIFT_PX_PER_SECOND = 26;
const reducedMotion = () => typeof window !== "undefined" && (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches || document.documentElement.classList.contains("reduce-motion"));

/**
 * One full-width row of tool cards that drifts slowly to the right (new tools slide in from the right edge) and loops.
 * Three copies of the cards sit in the row and the view lives in the middle one, so the loop never shows a gap, however wide the screen.
 * Touch swipes, mouse hover, keyboard focus and the arrows take over at once and the drift resumes a moment later;
 * a pause button and prefers-reduced-motion stop it for good. The extra copies are hidden from readers and the tab order.
 */
export function ToolCarousel({ position }: { position: "home_tools" | "tools_hub" }) {
  const track = useRef<HTMLDivElement>(null);
  const middleStart = useRef<HTMLAnchorElement>(null);
  const resumeAt = useRef(0);
  const settle = useRef(0);
  const [paused, setPaused] = useState(() => reducedMotion());
  const tools = orderedTools();
  /** Width of one copy of the cards (distance between the first cards of two neighbouring copies). */
  const copyWidth = () => (middleStart.current && track.current ? middleStart.current.offsetLeft - (track.current.firstElementChild as HTMLElement).offsetLeft : 0);
  const hold = (ms: number) => { resumeAt.current = ms === Infinity ? Infinity : performance.now() + ms; };
  /** Keeps the view inside the middle copy; an identical spot in a neighbour copy looks exactly the same. */
  const normalize = () => {
    const el = track.current, width = copyWidth();
    if (!el || width <= 0) return;
    let next = el.scrollLeft;
    while (next >= width * 2) next -= width;
    while (next < width) next += width;
    if (next !== el.scrollLeft) el.scrollLeft = next;
  };

  // Start in the middle copy (before the first paint) and stay there after a resize.
  useLayoutEffect(() => {
    normalize();
    window.addEventListener("resize", normalize);
    return () => window.removeEventListener("resize", normalize);
  }, []);

  useEffect(() => {
    const el = track.current;
    if (!el || paused) return;
    let raf = 0, last = 0, carry = 0;
    const tick = (now: number) => {
      const dt = last ? Math.min(1000, now - last) : 16;
      last = now;
      if (!document.hidden && now >= resumeAt.current) {
        carry += DRIFT_PX_PER_SECOND * dt / 1000;
        const whole = Math.floor(carry);
        if (whole >= 1) { carry -= whole; el.scrollLeft += whole; normalize(); }
      }
      raf = requestAnimationFrame(tick);
    };
    // Only run while the row is on screen.
    const start = () => { if (!raf) { last = 0; raf = requestAnimationFrame(tick); } };
    const stop = () => { cancelAnimationFrame(raf); raf = 0; };
    const observer = typeof IntersectionObserver === "function" ? new IntersectionObserver(([entry]) => (entry?.isIntersecting ? start() : stop())) : null;
    if (observer) observer.observe(el); else start();
    return () => { stop(); observer?.disconnect(); };
  }, [paused]);

  const step = (direction: 1 | -1) => {
    const el = track.current;
    if (!el) return;
    hold(4000);
    el.scrollBy({ left: direction * Math.max(240, el.clientWidth * 0.8), behavior: "smooth" });
  };
  // After a swipe or a smooth scroll has come to rest, move back into the middle copy (never in the middle of a gesture).
  const onScroll = () => { window.clearTimeout(settle.current); settle.current = window.setTimeout(normalize, 160); };
  const card = (tool: ReturnType<typeof orderedTools>[number], copy: 0 | 1 | 2) => <a key={`${copy}-${tool.slug}`} ref={copy === 1 && tool.slug === tools[0]?.slug ? middleStart : undefined}
    className="tool-carousel-card" href={lp(toolPath(tool.slug))} data-featured-tool={tool.slug} {...(copy === 1 ? {} : { "aria-hidden": true, tabIndex: -1 })}>
    <span className="tool-carousel-media"><ToolArt slug={tool.slug} sizes="260px" /></span>
    <span className="tool-carousel-copy">
      <strong>{tool.name}</strong>
      <span>{tool.summary}</span>
      <span className="tool-carousel-go">{t("免費試用")} <span aria-hidden="true">→</span></span>
    </span>
  </a>;
  return <div className="tool-carousel" data-cta-position={position}
    onPointerEnter={(event) => { if (event.pointerType === "mouse") hold(Infinity); }} onPointerLeave={(event) => { if (event.pointerType === "mouse") hold(900); }}
    onTouchStart={() => hold(Infinity)} onTouchEnd={() => hold(3500)} onTouchCancel={() => hold(3500)}
    onFocus={() => hold(Infinity)} onBlur={() => hold(1500)}>
    <button type="button" className="tool-carousel-arrow prev" onClick={() => step(-1)} aria-label={t("上一組")}><span aria-hidden="true">‹</span></button>
    <div className="tool-carousel-track" ref={track} onScroll={onScroll} tabIndex={0} role="region" aria-label={t("免費教師工具")}>
      {tools.map((tool) => card(tool, 0))}
      {tools.map((tool) => card(tool, 1))}
      {tools.map((tool) => card(tool, 2))}
    </div>
    <button type="button" className="tool-carousel-arrow next" onClick={() => step(1)} aria-label={t("下一組")}><span aria-hidden="true">›</span></button>
    <button type="button" className="tool-carousel-pause" onClick={() => setPaused((value) => !value)} aria-pressed={paused} aria-label={paused ? t("繼續自動滑動") : t("暫停自動滑動")}><svg viewBox="0 0 14 14" width="12" height="12" aria-hidden="true" focusable="false">{paused ? <path d="M3 1.5v11l9-5.5z" fill="currentColor" /> : <><rect x="2.5" y="1.5" width="3.2" height="11" rx="1" fill="currentColor" /><rect x="8.3" y="1.5" width="3.2" height="11" rx="1" fill="currentColor" /></>}</svg></button>
  </div>;
}
